import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaEdit,
  FaBan,
  FaDownload,
  FaPlus,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheck,
  FaTrash,
  FaSearch,
} from "react-icons/fa";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import CategoryFormModal from "./CategoryFormModal";
import CategoryViewModal from "./CategoryViewModal";
import Sidebar from "./Sidebar";
import { useScreenPermissions } from "./useScreenPermissions";

const LeftArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
    />
  </svg>
);
const RightArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c12.5-12.5 12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"
    />
  </svg>
);

const API_URL = "/api";

const CategoryMaster = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [status, setStatus] = useState("All");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  // States for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Get user permissions
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  useEffect(() => {
    fetchCategories();
  }, []);

  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/categories`);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      console.log("Categories API response:", data);
      // Ensure data is an array
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    if (!Array.isArray(categories)) return;

    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    const sortedCategories = [...categories].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;

      // Handle date fields
      if (key === "CreatedDate") {
        return direction === "asc"
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }

      // Handle numeric fields
      if (key === "CategoryCode") {
        const aNum = parseInt((a[key] || "").split("-")[1] || "0");
        const bNum = parseInt((b[key] || "").split("-")[1] || "0");
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }
      // Handle string fields
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setCategories(sortedCategories);
  };

  // Render sort arrows (copied from SalesOrder to ensure identical look/behavior)
  const renderSortArrows = (col) => {
    const active = sortConfig.key === col;
    return (
      <span className="ml-2 flex flex-col">
        <FaSortUp className={`h-3 w-3 ${active && sortConfig.direction === 'asc' ? 'text-blue-600' : 'text-gray-500'}`} />
        <FaSortDown className={`h-3 w-3 -mt-1.5 ${active && sortConfig.direction === 'desc' ? 'text-blue-600' : 'text-gray-500'}`} />
      </span>
    );
  };

  const handleView = (category) => {
    setSelectedCategory(category);
    setShowView(true);
  };

  const handleEdit = (category) => {
    setSelectedCategory(category);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDelete = async (category) => {
    if (!window.confirm("Are you sure you want to delete this category?"))
      return;

    try {
      const response = await fetch(
        `${API_URL}/categories/${category.CategoryCode}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete category");
      }

      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      alert(error.message || "An error occurred while deleting the category");
    }
  };

  const handleDownloadCSV = () => {
    if (!Array.isArray(categories) || categories.length === 0) {
      alert("No categories to export");
      return;
    }

    const headers = [
      "Category Code",
      "Category Name",
      "Description",
      "Subcategory Count",
      "Status",
      "Created Date",
    ];
    const rows = categories.map((c) => [
      c.CategoryCode,
      c.CategoryName,
      c.Description || "-",
      c.SubcategoryCount || 0,
      c.Status,
      dayjs(c.CreatedDate).format("YYYY-MM-DD HH:mm"),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "categories.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedCategory(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (isEdit && selectedCategory) {
        const response = await fetch(
          `${API_URL}/categories/${selectedCategory.CategoryCode}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
          }
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update category");
        }
      } else {
        const response = await fetch(`${API_URL}/categories`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create category");
        }
      }
      setShowForm(false);
      fetchCategories();
    } catch (error) {
      console.error("Error saving category:", error);
      alert(error.message || "An error occurred while saving the category");
    } finally {
      setFormLoading(false);
    }
  };
  const handleLogout = () => {
    navigate('/login');
  };
  

  // Filter categories based on search term and filters
  const filteredCategories = categories.filter((category) => {
    const matchesSearch =
      searchTerm === "" ||
      (category.CategoryCode && category.CategoryCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (category.CategoryName && category.CategoryName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (category.Description &&
        category.Description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = status === "All" || category.Status === status;

    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const categoriesForPage = filteredCategories.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPaginationButtons = () => {
    const pages = [];
    // Show a max of 5 page buttons
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    // Render "previous" button
    pages.push(
      <button
        key="prev"
        className="p-2 transition-all duration-200"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={{
          color: currentPage === 1 ? 'var(--disabled-color, #c7b1aa)' : 'var(--accent-color)',
          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
          background: 'transparent'
        }}
      >
        <LeftArrowIcon className="w-3 h-3" />
      </button>
    );

    // Render page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`px-4 py-1 font-medium transition-all duration-200`}
          onClick={() => handlePageChange(i)}
          style={{
            backgroundColor: i === currentPage ? 'var(--accent-color)' : 'transparent',
            color: i === currentPage ? '#000' : 'var(--text-primary)'
          }}
        >
          {i}
        </button>
      );
    }

    // Render "next" button
    pages.push(
      <button
        key="next"
        className="p-2 transition-all duration-200"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
        style={{
          color: (currentPage === totalPages || totalPages === 0) ? 'var(--disabled-color, #c7b1aa)' : 'var(--accent-color)',
          cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
          background: 'transparent'
        }}
      >
        <RightArrowIcon className="w-3 h-3" />
      </button>
    );

    return pages;
  };

  // Check permissions for actions
  const canCreate = hasFullAccess || hasPermission('CATEGORY', 'CanCreate');
  const canView = hasFullAccess || hasPermission('CATEGORY', 'CanView');
  const canEdit = hasFullAccess || hasPermission('CATEGORY', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('CATEGORY', 'CanDelete');

  // THEME OBJECT (passed to Sidebar so it matches this page)
  const pageTheme = {
    bg: '#fff7f2',
    sidebarBg: '#fff0e9',
    accent: '#ffc9b8',
    border: '#f3d9cf',
    text: '#3e2e26',
    muted: '#63514a',
    iconColor: '#4a352d',
    hoverBg: '#fff7f2',
  };

  useEffect(() => {
    const COLORS = {
      PAGE_BG: pageTheme.bg,
      HEADER_BG: pageTheme.sidebarBg,
      ACCENT: pageTheme.accent,
      DIVIDER: pageTheme.border,
      TEXT: pageTheme.text,
      MUTED: pageTheme.muted,
      ICON: pageTheme.iconColor,
      HOVER_BG: pageTheme.hoverBg,
    };

    const themeVars = {
      '--sidebar-line-color': COLORS.DIVIDER,
      '--sidebar-bg': pageTheme.sidebarBg,
      '--page-background': COLORS.PAGE_BG,
      '--accent-color': COLORS.ACCENT,
      '--accent-hover': COLORS.ACCENT,
      '--page-border-color': COLORS.DIVIDER,
      '--text-primary': COLORS.TEXT,
      '--text-muted': COLORS.MUTED,
      '--disabled-color': '#c7b1aa',
      '--search-icon': COLORS.MUTED,
      '--hover-gray': COLORS.HOVER_BG,
      '--icon-color': COLORS.ICON
    };

    Object.entries(themeVars).forEach(([k, v]) => {
      document.documentElement.style.setProperty(k, v);
    });

    const styleId = 'category-layout-overrides';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      /* Scoped layout rules for Category page (theme overrides copied from SubcategoryMaster) */
      .category-root { background: ${COLORS.PAGE_BG} !important; }

      .primary-btn { background-color: var(--accent-color); color: #ffffff; border-radius: 0; }
      .primary-btn:hover { background-color: var(--accent-hover); }

      .secondary-btn { background-color: #ffffff; color: var(--text-primary); border: 1px solid var(--page-border-color); border-radius: 0; }
      .secondary-btn:hover { background-color: var(--hover-gray); }

      .status-active { background-color: #b5dac8ff; color: #064e3b; border-radius: 9999px; padding: 4px 10px; font-size: 12px; display: inline-block; font-weight: 600; }
      .status-inactive { background-color: #fff7ed; color: #92400e; border-radius: 9999px; padding: 4px 10px; font-size: 12px; display: inline-block; font-weight: 600; }

      .cust-status{
        
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

          .cust-status-in{
         
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

      .accent-icon { color: var(--accent-color); }
      .accent-icon-hover:hover { color: var(--accent-hover); }

      .page-active { background-color: var(--accent-color); color: #fff; border-radius: 0; }
      .page-inactive { color: var(--accent-color); }
      .page-inactive:hover { background-color: var(--hover-gray); }

      .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--text-primary) !important; }
      .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item { color: var(--text-primary) !important; }

      .table-header-sort .sort-inactive { opacity: 0.85; color: #9aa7b0; }
      .table-header-sort .sort-active { opacity: 1; color: #155e52; }
    `;
    // keep the rest of css from original file (unchanged) - avoid duplication here
    document.head.appendChild(style);

    // intentionally no cleanup to allow consistent theme across pages
  }, [pageTheme]);

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: "var(--accent-color)", borderBottomColor: "#e6c8bd" }}></div>
          <p className="mt-4" style={{ color: 'var(--text-muted)' }}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex category-root" style={{ backgroundColor: pageTheme.bg }}>
      {/* pass the theme object to Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: 'black', profileBg: pageTheme.accent }}
        onLogout={handleLogout}
      />
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        {/* ---------- Header (matched to SalesOrder) ---------- */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b" style={{ borderColor: pageTheme.border, paddingBottom: 16 }}>
          <div className="flex-1">
            <h1 className="text-3xl font-bold" style={{ color: pageTheme.text }}>Category Master</h1>
            <p className="mt-1 text-base" style={{ color: pageTheme.muted }}>Manage your product categories and subcategories</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadCSV}
              className="flex items-center gap-2 px-5 py-2 bg-white border font-medium transition-all duration-200 shadow-sm"
              style={{ color: pageTheme.text, borderColor: pageTheme.border }}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>
            {canCreate && (
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm primary-btn"
                style={{ backgroundColor: pageTheme.accent, color: '#060606ff' }}
              >
                <FaPlus className="w-4 h-4" />
                Create New
              </button>
            )}
          </div>
        </header>

        {/* Stats Cards (kept same) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border shadow-md" style={{ borderColor: pageTheme.border }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: pageTheme.muted }}>Total Categories</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{categories.length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: pageTheme.sidebarBg, color: pageTheme.iconColor }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border shadow-md" style={{ borderColor: pageTheme.border }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: pageTheme.muted }}>Active Categories</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{categories.filter((c) => c.Status === "Active" || c.Status === "1").length}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#ecfdf5', color: '#16a34a' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border shadow-md" style={{ borderColor: pageTheme.border }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: pageTheme.muted }}>Total Subcategories</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{categories.reduce((sum, c) => sum + (c.SubcategoryCount || 0), 0)}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#fff7ed', color: '#b45309' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14v6m-3-3h6M6 10h2a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6a2 2 0 012-2zm10-4a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border shadow-md" style={{ borderColor: pageTheme.border }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: pageTheme.muted }}>This Month</p>
                <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>
                  {categories.filter((c) => {
                    const createdDate = new Date(c.CreatedDate);
                    const now = new Date();
                    return (
                      createdDate.getMonth() === now.getMonth() &&
                      createdDate.getFullYear() === now.getFullYear()
                    );
                  }).length}
                </p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#f3e8ff', color: '#7c3aed' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters (matched spacing & styles) */}
        <section className="p-5 bg-white border mb-6" style={{ borderColor: pageTheme.border }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: pageTheme.muted }}>What are you looking for?</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--search-icon)' }} />
                <input
                  type="text"
                  placeholder="Search for category code, name, description, etc"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200"
                  style={{ borderColor: pageTheme.border }}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: pageTheme.muted }}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: pageTheme.border }}
              >
                <option>All</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>
            <button
              onClick={() => { setCurrentPage(1); fetchCategories(); }}
              className="px-5 py-2 font-medium transition-all duration-200 shadow-sm primary-btn"
              style={{ backgroundColor: pageTheme.accent, color: '#000000ff' }}
            >
              Search
            </button>
          </div>
        </section>

        {/* Categories Table (layout matched to SalesOrder) */}
        <section className="bg-white border border-gray-300" style={{ borderColor: pageTheme.border }}>
          <div className="flex justify-between items-center p-4 border-b border-gray-300" style={{ borderColor: pageTheme.border }}>
            <h2 className="text-xl font-semibold" style={{ color: pageTheme.text }}>Category Summary</h2>
            <div className="flex gap-2">{renderPaginationButtons()}</div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs font-semibold uppercase" style={{ backgroundColor: '#ffefe6', color: pageTheme.muted }}>
                <tr>
                  {[
                    { key: 'CategoryCode', label: 'CATEGORY CODE' },
                    { key: 'CategoryName', label: 'CATEGORY NAME' },
                    { key: 'Description', label: 'DESCRIPTION' },
                    { key: 'SubcategoryCount', label: 'SUBCATEGORIES' },
                    { key: 'Status', label: 'STATUS' },
                    { key: 'CreatedDate', label: 'CREATED AT' },
                    { key: 'actions', label: 'ACTIONS' },
                  ].map((col) => (
                    <th
                      key={col.key}
                      className="px-6 py-3 text-left cursor-pointer transition-colors duration-200"
                      onClick={() => col.key !== 'actions' && handleSort(col.key)}
                      style={{ borderBottom: `1px solid ${pageTheme.border}` }}
                    >
                      <div className="flex items-center">
                        {col.label}
                        {col.key !== 'actions' && renderSortArrows(col.key)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: pageTheme.border }}>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10" style={{ color: pageTheme.muted, fontWeight: 600 }}>Loading categories...</td>
                  </tr>
                ) : filteredCategories.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10" style={{ color: pageTheme.muted, fontWeight: 600 }}>No categories found.</td>
                  </tr>
                ) : (
                  categoriesForPage.map((category, idx) => (
                    <tr key={category.CategoryCode ? category.CategoryCode.toUpperCase() : idx} className="transition-colors duration-200" style={{ backgroundColor: '#ffffff' }}>
                      <td className="px-6 py-3 font-medium" style={{ color: pageTheme.text }}>{category.CategoryCode ? category.CategoryCode.toUpperCase() : '-'}</td>
                      <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{category.CategoryName ? category.CategoryName.toUpperCase() : '-'}</td>
                      <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{category.Description ? category.Description.toUpperCase() : '-'}</td>
                      <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{category.SubcategoryCount || 0}</td>
                      <td className="px-6 py-3">
                        <span
                          className= {`  rounded-full ${category.Status === "1" || category.Status === "Active"  ? 'cust-status bg-teal-50 text-teal-700 hover:bg-teal-100 font-bold' : 'cust-status-in bg-amber-50 text-amber-700 hover:bg-amber-100 font-bold'}`}>
                          {category.Status === "1" || category.Status === "Active" ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-3" style={{ color: pageTheme.muted }}>{category.CreatedDate ? dayjs(category.CreatedDate).format('DD MMM YYYY') + ' ' + dayjs(category.CreatedDate).format('HH:mm') : '-'}</td>
                      <td className="px-6 py-3">
                        <div className="flex gap-3 justify-center">
                          {canView && (
                            <button onClick={() => handleView(category)} title="View" className="transition-colors duration-200" style={{ color: pageTheme.accent }}>
                              <FaEye className="h-4 w-4" />
                            </button>
                          )}
                          {canEdit && (
                            <button onClick={() => handleEdit(category)} title="Edit" className="transition-colors duration-200" style={{ color: '#16a34a' }}>
                              <FaEdit className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => handleDelete(category)} title="Delete" className="transition-colors duration-200" style={{ color: '#dc2626' }}>
                              <FaTrash className="h-4 w-4" />
                            </button>
                          )}
                          <button onClick={handleDownloadCSV} title="Download CSV" className="transition-colors duration-200" style={{ color: '#7c3aed' }}>
                            <FaDownload className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Modals */}
        <CategoryFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedCategory}
          isEdit={isEdit}
          loading={formLoading}
        />
        <CategoryViewModal
          open={showView}
          onClose={() => setShowView(false)}
          category={selectedCategory}
        />
      </main>
    </div>
  );
};

export default CategoryMaster;
